import { colors as themeColors } from '../utils/theme';
import { colors } from '../utils/theme';
import React, { useState, useRef, useReducer, useEffect, useMemo } from "react";
import { Box, Button, Container, Typography, Snackbar, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, FormGroup, TextField, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarDensitySelector,
    GridToolbarFilterButton,
    GridToolbarExport,       // don't use this in the toolbar, shown only for reference
    GridToolbarQuickFilter
} from '@mui/x-data-grid';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import CancelIcon from "@mui/icons-material/Cancel";
import SettingsIcon from "@mui/icons-material/Settings";

// Base URL for the backend API
const path = `http://192.168.1.248:3109`;

// Simple UUID-like ID generator for unique row IDs
const generateUniqueId = () => { return `row-${Math.random().toString(36).substr(2, 9)}-${Date.now().toString(36)}`; };

// Reducer for dialog form state
const dialogInitialState = {
    selectedOptions: [],
    isWhatsAppKeyEnabled: false,
    isMobileAppEnabled: false,
    MobileAppUsers: "",
    WAKey: "",
    ProductKey: ""
};

// Custom toolbar WITHOUT export
function CustomToolbar(props) {
    // allow passing quickFilterProps via componentsProps.toolbar.quickFilterProps
    const quickFilterProps = (props?.quickFilterProps) || { debounceMs: 500 };

    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            {/* quick filter */}
            <div style={{ marginLeft: '8px', flex: 1 }}>
                <GridToolbarQuickFilter {...quickFilterProps} />
            </div>
            {/* intentionally omit GridToolbarExport to hide CSV/print/export */}
        </GridToolbarContainer>
    );
}

function dialogReducer(state, action) {
    switch (action.type) {
        case "UPDATE_FIELD":
            return { ...state, [action.field]: action.value };
        case "TOGGLE_OPTION": {
            const optStr = String(action.option);
            const has = state.selectedOptions.map(String).includes(optStr);
            return { ...state, selectedOptions: has ? state.selectedOptions.map(String).filter(o => o !== optStr) : [...state.selectedOptions.map(String), optStr] };
        }
        case "SET_FORM":
            return { ...action.payload, selectedOptions: (action.payload.selectedOptions || []).map(String) };
        case "RESET":
            return { ...dialogInitialState };
        default:
            return state;
    }
}

/**
 * decryptProductKey
 * Rule:
 * 1) first 5 chars => prefix
 * 2) chars at positions 6-7 => decimal count (n)
 * 3) extract last n characters from the key
 * 4) if extracted length is even => split into 2-char chunks -> selectedOptions (strings)
 *    else => whole extracted string as single token
 */
function decryptProductKey(rawKey = "") {
    const key = (rawKey || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (key.length < 7) return { prefix: "", count: 0, extracted: "", selectedOptions: [] };

    const prefix = key.slice(0, 5);
    const countStr = key.slice(5, 7);
    const count = parseInt(countStr, 10) || 0;
    const extracted = count > 0 ? key.slice(-count) : "";

    const selectedOptions = [];
    if (extracted) {
        if (extracted.length % 2 === 0) {
            for (let i = 0; i < extracted.length; i += 2) {
                selectedOptions.push(extracted.slice(i, i + 2));
            }
        } else {
            selectedOptions.push(extracted);
        }
    }

    return { prefix, count, extracted, selectedOptions };
}

const FileUpload = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]); // State for dynamic columns
    const [uploading, setUploading] = useState(false);
    const [generatingKeys, setGeneratingKeys] = useState(false);
    const [keysGenerated, setKeysGenerated] = useState(false);
    const [dataInserted, setDataInserted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [generateProgress, setGenerateProgress] = useState(0);
    const [progressValue, setProgressValue] = useState(0);
    const [bufferValue, setBufferValue] = useState(0);
    const [showBuffer, setShowBuffer] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [checkboxOptions, setCheckboxOptions] = useState([]);
    const [whatsAppKeyLoading, setWhatsAppKeyLoading] = useState(false);
    const [productKeyLoading, setProductKeyLoading] = useState(false);
    const [dialogForm, dispatch] = useReducer(dialogReducer, dialogInitialState);
    const [isNavigating, setIsNavigating] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);

    // Fetch feature options
    useEffect(() => {
        axios.get(`${path}/api/features-list`).then(r => {
            console.log(r.data);
            setCheckboxOptions(r.data);
        }).catch(console.error);
    }, []);

    // Handle page reload prevention
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (file || data.length > 0) {
                event.preventDefault();
                event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
                return "You have unsaved changes. Are you sure you want to leave?";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [file, data]);

    // Handle back navigation
    const handleBackClick = (event) => {
        if (file || data.length > 0) {
            event.preventDefault();
            const confirm = window.confirm("You have unsaved changes. Are you sure you want to leave?");
            if (confirm) {
                navigate("/main/key-options");
            }
        } else {
            navigate("/main/key-options");
        }
    };

    // Update progress
    const updateProgress = (value) => {
        const clipped = Math.max(0, Math.min(100, value));
        setProgressValue(clipped);
        setBufferValue(Math.min(100, clipped + 10));
    };

    // Helper: normalize header text (trim + lower-case)
    const normalizeHeader = (h) => (h || "").toString().trim().toLowerCase();

    // Helper: convert header to field name (camelCase)
    const headerToField = (header) => {
        return header
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .split(' ')
            .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
    };

    // Helper: convert header to display name
    const headerToDisplayName = (header) => {
        return header
            .toString()
            .trim()
            .replace(/([A-Z])/g, ' $1')
            .replace(/[^a-zA-Z0-9]+/g, ' ')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Process the uploaded file
    const processFile = (file) => {
        const reader = new FileReader();

        reader.onprogress = (e) => {
            if (e.lengthComputable) {
                const percentRead = Math.round((e.loaded / e.total) * 100);
                const mapped = Math.round(percentRead * 0.5);
                updateProgress(mapped);
            }
        };

        reader.onload = (e) => {
            try {
                const binaryData = e.target.result;
                const workbook = XLSX.read(binaryData, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                if (!jsonData || jsonData.length === 0) {
                    throw new Error("Empty sheet");
                }

                // Get header row and normalize
                const headersRaw = jsonData[0] || [];
                const headersNorm = headersRaw.map(normalizeHeader);

                // Define supported header formats for validation
                const formatA = ["company name", "client name", "mobileno", "productkey"];
                const formatB = ["product key", "company name", "number", "no of company"];
                const isFormatA = formatA.every((h, i) => headersNorm[i] === h);
                const isFormatB = formatB.every((h, i) => headersNorm[i] === h);

                if (!isFormatA && !isFormatB) {
                    setSnackbar({
                        open: true,
                        message: "Invalid file format. Expected headers either: (Company Name, Client Name, MobileNo, ProductKey) OR (PRODUCT KEY, COMPANY NAME, NUMBER, NO OF COMPANY)",
                        severity: "error"
                    });
                    setData([]);
                    setColumns([]);
                    setKeysGenerated(false);
                    setDataInserted(false);
                    updateProgress(0);
                    setShowBuffer(false);
                    return;
                }

                // Create dynamic columns based on Excel headers
                const dynamicColumns = headersRaw.map(header => ({
                    field: headerToField(header),
                    headerName: headerToDisplayName(header),
                    flex: 1,
                    minWidth: 140,
                    renderCell: (params) => params.value || "-"
                }));

                // Add static columns
                const staticColumns = [
                    { field: "OldKey", headerName: "Old Key", flex: 1.2, minWidth: 140, renderCell: (params) => params.value || "-" },
                    { field: "ProductKey", headerName: "New Product Key", flex: 1.4, minWidth: 160 },
                    { field: "WAKey", headerName: "WA Key", flex: 1.6, minWidth: 160, renderCell: (params) => params.value || "-" }
                ];

                // Combine dynamic and static columns, avoiding duplicates
                const allColumns = [
                    ...dynamicColumns.filter(col => !['OldKey', 'ProductKey', 'WAKey'].includes(col.field)),
                    ...staticColumns
                ];

                setColumns(allColumns);

                // Parse data rows
                const parsedData = jsonData.slice(1).map((row, rowIndex) => {
                    const c = (idx) => (row && row[idx] !== undefined ? row[idx] : "");

                    let rec = {
                        id: generateUniqueId(),
                        OldKey: "",
                        ProductKey: "",
                        WAKey: "",
                        selectedOptions: [],
                        isWhatsAppKeyEnabled: false,
                        isMobileAppEnabled: false,
                        MobileAppUsers: ""
                    };

                    // Map Excel headers to row fields
                    headersRaw.forEach((header, idx) => {
                        rec[headerToField(header)] = String(c(idx) || "").trim();
                    });

                    if (isFormatA) {
                        // Format A mapping
                        rec.CompanyName = String(c(0) || "").trim();
                        rec.ContactPerson = String(c(1) || "").trim();
                        rec.MobileNo = String(c(2) || "").trim();
                        rec.OldKey = String(c(3) || "").trim();
                    } else {
                        // Format B mapping
                        rec.OldKey = String(c(0) || "").trim(); // Treat ProductKey column as OldKey
                        rec.CompanyName = String(c(1) || "").trim();
                        rec.MobileNo = String(c(2) || "").trim();
                        const maybeCount = c(3);
                        const parsedCount = (maybeCount === null || maybeCount === undefined || maybeCount === "") ? "" : String(maybeCount).trim();
                        rec.MobileAppUsers = parsedCount !== "" && /^\d+$/.test(parsedCount) ? parsedCount : "";
                        rec.isMobileAppEnabled = rec.MobileAppUsers !== "";
                    }

                    // Decrypt OldKey if present
                    if (rec.OldKey && String(rec.OldKey).trim() !== "") {
                        const decryption = decryptProductKey(rec.OldKey);
                        rec.selectedOptions = Array.isArray(decryption.selectedOptions) ? decryption.selectedOptions : [];
                        rec.__decryption = decryption;
                    } else {
                        rec.__decryption = { prefix: "", count: 0, extracted: "", selectedOptions: [] };
                    }

                    return rec;
                }).filter(row => row.CompanyName || row.ContactPerson || row.MobileNo || row.OldKey);

                setData(parsedData);

                console.group(`Parsed ${parsedData.length} records from ${file.name}`);
                parsedData.forEach((r, i) => {
                    console.log(`Row ${i}:`, r);
                });
                console.groupEnd();

                updateProgress(50);
                setSnackbar({ open: true, message: `Successfully parsed ${parsedData.length} records`, severity: "success" });

                updateProgress(100);
                setTimeout(() => {
                    setShowBuffer(false);
                    updateProgress(0);
                }, 800);
            } catch (err) {
                console.error(err);
                setSnackbar({ open: true, message: "Error parsing file", severity: "error" });
                setData([]);
                setColumns([]);
                setKeysGenerated(false);
                setDataInserted(false);
                updateProgress(0);
                setShowBuffer(false);
            }
        };

        reader.onerror = () => {
            setSnackbar({ open: true, message: "Error reading file", severity: "error" });
            setData([]);
            setColumns([]);
            setKeysGenerated(false);
            setDataInserted(false);
            updateProgress(0);
            setShowBuffer(false);
        };

        reader.readAsBinaryString(file);
    };

    // Open the feature dialog
    const handleFeatureConfigClick = () => {
        dispatch({
            type: "SET_FORM",
            payload: {
                selectedOptions: [],
                isWhatsAppKeyEnabled: false,
                isMobileAppEnabled: false,
                MobileAppUsers: "",
                WAKey: "",
                ProductKey: ""
            }
        });
        setDialogOpen(true);
    };

    // Handle dialog save
    const handleDialogSave = () => {
        const updatedData = data.map(row => ({
            ...row,
            selectedOptions: [...new Set([...row.selectedOptions, ...dialogForm.selectedOptions])],
            isWhatsAppKeyEnabled: dialogForm.isWhatsAppKeyEnabled,
            isMobileAppEnabled: dialogForm.isMobileAppEnabled,
            MobileAppUsers: dialogForm.isMobileAppEnabled ? dialogForm.MobileAppUsers : "",
            WAKey: dialogForm.isWhatsAppKeyEnabled ? dialogForm.WAKey : row.WAKey
        }));
        setData(updatedData);
        setDialogOpen(false);
        setSnackbar({ open: true, message: "Features applied to all rows", severity: "success" });
        setKeysGenerated(false);
    };

    // Key generation handlers
    const handleGenerateWhatsAppKey = async (selectedOptions) => {
        setWhatsAppKeyLoading(true);
        try {
            const { WAKey } = (await axios.post(`${path}/api/members/generate-whatsapp-key`, { selectedOptions })).data;
            return WAKey;
        } catch (err) {
            console.error("Error generating WhatsApp Key:", err);
            setSnackbar({ open: true, message: "Error generating WhatsApp Key", severity: "error" });
            return "";
        } finally {
            setWhatsAppKeyLoading(false);
        }
    };

    const handleGenerateProductKey = async () => {
        const selected = Array.from(new Set((dialogForm.selectedOptions || []).map(String)));
        if (!selected.length) {
            setSnackbar({ open: true, message: "Select at least one feature to generate Product Key", severity: "error" });
            return;
        }
        setProductKeyLoading(true);
        try {
            const { ProductKey } = (await axios.post(`${path}/api/members/generate-key`, { selectedOptions: selected })).data;
            dispatch({ type: "UPDATE_FIELD", field: "ProductKey", value: ProductKey });
            setSnackbar({ open: true, message: "Product Key generated successfully", severity: "success" });
        } catch (err) {
            console.error("Error generating Product Key:", err);
            setSnackbar({ open: true, message: "Error generating Product Key", severity: "error" });
        } finally {
            setProductKeyLoading(false);
        }
    };

    // Generate new keys for all records
    const handleGenerateAllKeys = async () => {
        if (!data.length) {
            setSnackbar({ open: true, message: "No data to generate keys for", severity: "error" });
            return;
        }

        setGeneratingKeys(true);
        setGenerateProgress(0);
        setShowBuffer(true);
        setKeysGenerated(false);
        setDataInserted(false);

        try {
            const updatedData = data.map(row => ({ ...row }));
            const totalRecords = data.length;

            for (let i = 0; i < totalRecords; i++) {
                const row = updatedData[i];

                if (!Array.isArray(row.selectedOptions) || row.selectedOptions.length === 0) {
                    console.warn(`Skipping row ${i} (ID: ${row.id}): No selected options`);
                    continue;
                }

                try {
                    const response = await axios.post(`${path}/api/members/generate-key`, { selectedOptions: row.selectedOptions });
                    const { ProductKey } = response.data || {};

                    let newWAKey = row.isWhatsAppKeyEnabled;
                    // console.log('NewWAKey', row);
                    if (row.isWhatsAppKeyEnabled ) {
                        newWAKey = await handleGenerateWhatsAppKey(row.selectedOptions);
                    }

                    if (ProductKey) {
                        const snapshotOld = (row.OldKey && String(row.OldKey).trim() !== "") ? row.OldKey : (row.ProductKey || "");
                        updatedData[i] = {
                            ...row,
                            OldKey: snapshotOld,
                            ProductKey: ProductKey,
                            WAKey: newWAKey
                        };
                    } else {
                        console.warn(`No ProductKey returned for row ${i}`);
                    }

                    const pct = Math.round(((i + 1) / totalRecords) * 100);
                    setGenerateProgress(pct);
                    updateProgress(pct);
                } catch (err) {
                    console.error(`Error generating keys for row ${i} (ID: ${row.id}):`, err);
                    setSnackbar({ open: true, message: `Error generating keys for row ${i + 1}`, severity: "error" });
                }
            }

            setData(updatedData);
            setKeysGenerated(true);
            setSnackbar({ open: true, message: "All keys generated successfully", severity: "success" });
        } catch (err) {
            console.error("Error during key generation:", err);
            setSnackbar({ open: true, message: "Error generating keys", severity: "error" });
        } finally {
            setGeneratingKeys(false);
            setGenerateProgress(0);
            updateProgress(100);
            setTimeout(() => {
                setShowBuffer(false);
                updateProgress(0);
            }, 800);
        }
    };

    // Handle upload to backend
    const handleUploadSubmit = async () => {
        if (!data.length) {
            setSnackbar({ open: true, message: "No valid data to upload", severity: "error" });
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        updateProgress(50);
        setShowBuffer(true);

        try {
            const payload = data.map(row => {
                const rowData = {
                    CompanyName: row.CompanyName,
                    ContactPerson: row.ContactPerson,
                    MobileNo: row.MobileNo,
                    ProductKey: row.ProductKey,
                    OldKey: row.OldKey,
                    selectedOptions: Array.isArray(row.selectedOptions) ? row.selectedOptions : [],
                    isWhatsAppKeyEnabled: row.isWhatsAppKeyEnabled,
                    isMobileAppEnabled: row.isMobileAppEnabled,
                    MobileAppUsers: row.isMobileAppEnabled && row.MobileAppUsers ? parseInt(row.MobileAppUsers, 10) : null,
                    noOfCompany: row.noOfCompany || null
                };
                // Include WAKey only if present in DataGrid
                if (row.WAKey && String(row.WAKey).trim() !== "") {
                    rowData.WAKey = row.WAKey;
                }
                // Include all dynamic fields from Excel
                columns.forEach(col => {
                    if (!['OldKey', 'ProductKey', 'WAKey', 'id', 'selectedOptions', 'isWhatsAppKeyEnabled', 'isMobileAppEnabled', 'MobileAppUsers', '__decryption', 'noOfCompany'].includes(col.field)) {
                        rowData[col.field] = row[col.field] || "";
                    }
                });
                return rowData;
            });

            console.log("Uploading payload:", payload);

            const response = await axios.post(`${path}/api/members/bulk-insert`, payload, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                    setUploadProgress(percentCompleted);
                    const overall = 50 + Math.round(percentCompleted * 0.5);
                    updateProgress(overall);
                }
            });

            const serverItems = response?.data?.data || [];
            console.log(serverItems)

            if (response.status === 201) {
                const newLocalData = applyServerResponseToLocalData(data, serverItems);
                setData(newLocalData);
                setSnackbar({ open: true, message: `Successfully uploaded ${data.length} records`, severity: "success" });
                setDataInserted(true);
                setFile(null);
                fileInputRef.current.value = "";
                updateProgress(100);

                if (Array.isArray(response.data.errors) && response.data.errors.length) {
                    console.warn("Server reported errors:", response.data.errors);
                }

                setTimeout(() => {
                    setShowBuffer(false);
                    updateProgress(0);
                }, 800);
            } else {
                setSnackbar({ open: true, message: response.data.message || "Failed to upload data", severity: "error" });
            }
        } catch (error) {
            console.error("Upload error:", error.response?.data || error.message);
            setSnackbar({ open: true, message: "Failed to upload data to server", severity: "error" });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Apply server response to local data
    const applyServerResponseToLocalData = (localData, serverItems) => {
        const norm = (v) => (v === undefined || v === null) ? '' : String(v).trim();

        const updated = localData.map(row => ({ ...row }));

        for (const item of serverItems || []) {
            const op = (item.operation || (item.Id ? 'update' : 'insert')).toLowerCase();
            const serverPrevRaw = (item.PreviousProductKeys === null || item.PreviousProductKeys === undefined) ? "" : String(item.PreviousProductKeys);
            const serverPrevList = serverPrevRaw ? serverPrevRaw.split(',').map(s => s.trim()).filter(Boolean).map(s => norm(s)) : [];

            const matchIndex = updated.findIndex(row => {
                if (item.MobileNo && row.MobileNo && norm(item.MobileNo) === norm(row.MobileNo)) return true;
                if (item.CustomerId && row.CustomerId && String(item.CustomerId) === String(row.CustomerId)) return true;
                if (serverPrevList.length) {
                    if (row.ProductKey && serverPrevList.includes(norm(row.ProductKey))) return true;
                    if (row.OldKey && serverPrevList.includes(norm(row.OldKey))) return true;
                }
                if (item.ProductKey && row.ProductKey && norm(item.ProductKey) === norm(row.ProductKey)) return true;
                return false;
            });

            if (matchIndex === -1) {
                continue;
            }

            const localRow = updated[matchIndex];
            const previousLocalPk = norm(localRow.ProductKey || "");

            const newProductKey = (item.ProductKey !== undefined && item.ProductKey !== null) ? String(item.ProductKey).trim() : localRow.ProductKey;

            let newOldKey = localRow.OldKey || "";
            if (serverPrevList.length) {
                newOldKey = serverPrevList[serverPrevList.length - 1] || newOldKey;
            } else {
                if (newProductKey && newProductKey !== previousLocalPk) {
                    newOldKey = localRow.OldKey || previousLocalPk || "";
                } else {
                    newOldKey = localRow.OldKey || "";
                }
            }

            updated[matchIndex] = {
                ...localRow,
                ProductKey: newProductKey,
                OldKey: newOldKey,
                WAKey: (item.WAKey !== undefined && item.WAKey !== null) ? String(item.WAKey) : (localRow.WAKey || ""),
                CustomerId: (item.CustomerId !== undefined && item.CustomerId !== null) ? item.CustomerId : localRow.CustomerId,
                PreviousProductKeys: serverPrevRaw !== "" ? serverPrevRaw : (localRow.PreviousProductKeys || ""),
                __dbId: item.Id || localRow.__dbId
            };
        }

        return updated;
    };

    // Export data to Excel
    const handleExport = () => {
        const exportData = data.map(row => {
            const rowData = {};
            columns.forEach(col => {
                rowData[col.headerName] = row[col.field] || "-";
            });
            rowData["Old Key"] = row.OldKey || "-";
            rowData["New Product Key"] = row.ProductKey || "-";
            rowData["WA Key"] = row.WAKey || "-";
            rowData["Is WhatsApp Key Enabled"] = row.isWhatsAppKeyEnabled ? "Yes" : "No";
            rowData["Is Mobile App Enabled"] = row.isMobileAppEnabled ? "Yes" : "No";
            rowData["Mobile App Users"] = row.isMobileAppEnabled && row.MobileAppUsers ? row.MobileAppUsers : "-";
            return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ClientData");
        XLSX.writeFile(workbook, `ClientData_${new Date().toISOString().slice(0, 10)}.xlsx`);
        setSnackbar({ open: true, message: "Data exported successfully", severity: "success" });
    };

    const handleMobileAppToggle = (checked) => {
        dispatch({ type: "UPDATE_FIELD", field: "isMobileAppEnabled", value: checked });
        if (checked && (!dialogForm.MobileAppUsers || !/^\d+$/.test(dialogForm.MobileAppUsers) || parseInt(dialogForm.MobileAppUsers, 10) < 1)) {
            dispatch({ type: "UPDATE_FIELD", field: "MobileAppUsers", value: "1" });
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch({ type: "RESET" });
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const validExtensions = [".xlsx", ".xls", ".csv"];
            const fileExtension = selectedFile.name.slice(selectedFile.name.lastIndexOf(".")).toLowerCase();
            if (!validExtensions.includes(fileExtension)) {
                setSnackbar({ open: true, message: "Only Excel (.xlsx, .xls) or CSV (.csv) files are allowed", severity: "error" });
                setFile(null);
                setData([]);
                setKeysGenerated(false);
                setDataInserted(false);
                fileInputRef.current.value = "";
                return;
            }
            setFile(selectedFile);
            setShowBuffer(true);
            setKeysGenerated(false);
            setDataInserted(false);
            updateProgress(3);
            processFile(selectedFile);
        }
    };

    const compactInputStyle = { "& .MuiInputBase-root": { height: 40 }, "& .MuiInputLabel-root": { fontSize: "0.75rem" }, mt: 0.6 };

    const dataGridKey = useMemo(() => data.map(row => row.id).join('-'), [data]);

    return (
        <Container sx={{ pt: 3, maxWidth: "1400px !important" }}>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: colors.primary.main }}> Upload Client Data </Typography>
                <Button variant="contained" onClick={handleBackClick} sx={{ height: 40, bgcolor: colors.primary.main, "&:hover": { bgcolor: "#145db5" }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2 }} > Back to Key Options </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: { xs: "stretch", sm: "center" } }}>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} ref={fileInputRef} style={{ display: "none" }} id="file-upload" />
                    <Button variant="contained" onClick={() => fileInputRef.current.click()} sx={{ height: 40, bgcolor: colors.primary.main, "&:hover": { bgcolor: "#145db5" }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2 }}>
                        Choose File
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ color: themeColors.neutral.sub }}>
                            {file ? file.name : "No file selected (Excel or CSV only)"}
                        </Typography>
                        {(file || (dataInserted && data.length > 0)) && (
                            <Button onClick={handleFeatureConfigClick} sx={{ minWidth: 0, p: 0.5, color: themeColors.neutral.sub, "&:hover": { color: colors.primary.main } }}>
                                <SettingsIcon />
                            </Button>
                        )}
                    </Box>

                    {(file || (dataInserted && data.length > 0)) && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button variant="contained" onClick={handleGenerateAllKeys} disabled={generatingKeys || uploading || !data.length} sx={{ height: 40, bgcolor: colors.primary.main, "&:hover": { bgcolor: colors.primary.dark }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2, "&.Mui-disabled": { bgcolor: themeColors.neutral.lineStrong, color: themeColors.neutral.sub } }} >
                                {generatingKeys ? <CircularProgress size={24} color="inherit" /> : "Generate New Keys"}
                            </Button>
                            <Button variant="contained" onClick={handleUploadSubmit} disabled={uploading || !data.length || !keysGenerated} sx={{ height: 40, bgcolor: colors.primary.main, "&:hover": { bgcolor: colors.primary.dark }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2, "&.Mui-disabled": { bgcolor: themeColors.neutral.lineStrong, color: themeColors.neutral.sub } }} >
                                {uploading ? <CircularProgress size={24} color="inherit" /> : "Insert"}
                            </Button>
                            {dataInserted && data.length > 0 && (
                                <Button variant="contained" onClick={handleExport} sx={{ height: 40, bgcolor: themeColors.success.main, "&:hover": { bgcolor: "#388e3c" }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2 }} >
                                    Export
                                </Button>
                            )}
                            <Box sx={{ width: 240 }}>
                                {showBuffer ? (
                                    <>
                                        <LinearProgress variant="buffer" value={progressValue} valueBuffer={bufferValue} sx={{ height: 8, borderRadius: 4 }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, textAlign: "center" }}>
                                            {generatingKeys ? `Generating: ${generateProgress}%` : uploading ? `Uploading: ${uploadProgress}%` : `${progressValue}%`}
                                        </Typography>
                                    </>
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {/* <CircularProgress size={20} />
                                        <Typography variant="caption">Preparing…</Typography> */}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>

                {data.length > 0 && (
                    <Box sx={{ maxHeight: "80vh", maxWidth:'120%' }}>
                        <DataGrid
                            key={dataGridKey}
                            rows={data}
                            columns={columns}
                            pageSize={70}
                            rowsPerPageOptions={[70, 150, 250]}
                            autoHeight
                            components={{ Toolbar: CustomToolbar }}
                            componentsProps={{
                                toolbar: {
                                    quickFilterProps: { debounceMs: 500 } // this will be forwarded to CustomToolbar
                                }
                            }}
                            sx={{
                                borderRadius: 2,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.primary.dark, color: themeColors.neutral.surface, fontWeight: 'bold' },
                                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer': { color: themeColors.neutral.surface },
                                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: `${themeColors.neutral.surface} !important`, opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiIconButton-root': { color: `${themeColors.neutral.surface} !important`, opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': { color: `${themeColors.neutral.surface} !important`, opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg path': { fill: `${themeColors.neutral.surface} !important`, stroke: `${themeColors.neutral.surface} !important`, color: `${themeColors.neutral.surface} !important`, opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon svg path, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon svg path': { fill: `${themeColors.neutral.surface} !important`, stroke: `${themeColors.neutral.surface} !important`, opacity: 1 },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.primary.tint },
                                '& .highlight-row': { animation: 'colorTransition 2s infinite alternate' },
                                '@keyframes colorTransition': { '0%': { backgroundColor: '#FDB68B' }, '100%': { backgroundColor: '#F4A261' } },
                                '& .mobile-missing': { animation: 'mobilePulse 3s ease-in-out infinite' },
                                '@keyframes mobilePulse': { '0%': { backgroundColor: themeColors.warning.tint }, '100%': { backgroundColor: '#ffb74d' } },
                                '& .mobile-zero': { animation: 'mobileZeroPulse 2.8s ease-in-out infinite' },
                                '@keyframes mobileZeroPulse': { '0%': { backgroundColor: '#fff8e1' }, '50%': { backgroundColor: '#ffcc80' }, '100%': { backgroundColor: themeColors.warning.main } },
                                '& .client-missing': { animation: 'clientPulse 2.6s ease-in-out infinite' },
                                '@keyframes clientPulse': { '0%': { backgroundColor: '#fff3f3' }, '50%': { backgroundColor: '#ffd6d6' }, '100%': { backgroundColor: '#ffb3b3' } },
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                            }}
                            getRowClassName={(params) => {
                                const name = params.row.ContactPerson;
                                const mobile = params.row.MobileNo;
                                if (mobile === undefined || mobile === null || (String(mobile).trim() === '')) {
                                    return 'mobile-missing';
                                }
                                if (String(mobile).trim() === '0') {
                                    return 'mobile-zero';
                                }
                                if (!params.row.OldKey) return 'highlight-row';
                                return '';
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth sx={{ "& .MuiDialog-paper": { borderRadius: 4, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", backgroundColor: themeColors.neutral.surfaceAlt, p: 2 } }}>
                <DialogTitle sx={{ fontWeight: 600, fontSize: "1.5rem", color: colors.primary.main, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, borderBottom: `1px solid ${themeColors.neutral.line}` }}>
                    Add Ons Options
                    <Button onClick={handleDialogClose} sx={{ minWidth: 0, p: 0.5, color: themeColors.neutral.sub, "&:hover": { color: themeColors.error.dark } }}>
                        <CancelIcon />
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>LAN Version</Typography>
                        <FormControlLabel control={<Checkbox size="small" checked={dialogForm.selectedOptions.includes("99")} onChange={() => dispatch({ type: "TOGGLE_OPTION", option: "99" })} />} label="LAN Version?" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                            <FormControlLabel label="WhatsApp Key"
                                control={
                                    <Checkbox size="small" checked={dialogForm.isWhatsAppKeyEnabled} disabled={whatsAppKeyLoading}
                                        onChange={(e) => {
                                            dispatch({ type: "UPDATE_FIELD", field: "isWhatsAppKeyEnabled", value: e.target.checked });
                                            if (e.target.checked) {
                                                handleGenerateWhatsAppKey(dialogForm.selectedOptions);
                                            } else {
                                                dispatch({ type: "UPDATE_FIELD", field: "WAKey", value: "" });
                                            }
                                        }}
                                    />
                                }
                            />
                            {/* {dialogForm.isWhatsAppKeyEnabled && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField size="small" label="WA Key" value={dialogForm.WAKey || ""} disabled fullWidth sx={compactInputStyle} />
                                    {whatsAppKeyLoading && <CircularProgress size={24} />}
                                </Box>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <TextField size="small" label="Product Key" value={dialogForm.ProductKey || ""} disabled fullWidth sx={compactInputStyle} />
                                <Button variant="contained" onClick={handleGenerateProductKey} disabled={productKeyLoading} sx={{ height: 40, whiteSpace: "nowrap" }} >
                                    {productKeyLoading ? <CircularProgress size={24} color="inherit" /> : "Generate Key"}
                                </Button>
                            </Box> */}
                            <FormControlLabel control={<Checkbox size="small" checked={dialogForm.isMobileAppEnabled} onChange={(e) => handleMobileAppToggle(e.target.checked)} />} label="Mobile App" />
                            {dialogForm.isMobileAppEnabled && (
                                <TextField size="small" label="Number of Mobile App Users" value={dialogForm.MobileAppUsers} fullWidth type="number" inputProps={{ min: 1, step: 1, pattern: "\\d*" }} sx={compactInputStyle}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "" || (/^\d+$/.test(v) && parseInt(v, 10) >= 1)) {
                                            dispatch({ type: "UPDATE_FIELD", field: "MobileAppUsers", value: v });
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Select Features:</Typography>
                        <FormGroup sx={{ display: "grid", gridTemplateColumns: "1fr", rowGap: 0.5, "& .MuiFormControlLabel-root": { alignItems: "center", margin: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }, "& .MuiCheckbox-root": { p: 0.5 } }}>
                            {checkboxOptions.map(opt => {
                                const optVal = String(opt.ModuleCode ?? opt.ModuleCode);
                                const checked = Array.isArray(dialogForm.selectedOptions) && dialogForm.selectedOptions.map(String).includes(optVal);
                                return (
                                    <FormControlLabel key={opt.Id ?? optVal} label={opt.Label ?? String(opt.ModuleCode)}
                                        control={ <Checkbox size="small" checked={checked} onChange={() => dispatch({ type: "TOGGLE_OPTION", option: optVal })} inputProps={{ 'aria-label': `feature-${optVal}` }} /> }
                                    />
                                );
                            })}
                        </FormGroup>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${themeColors.neutral.line}`, backgroundColor: themeColors.neutral.surface, borderRadius: "0 0 12px 12px" }}>
                    <Button onClick={handleDialogClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none", fontWeight: "medium", color: themeColors.neutral.sub, borderColor: themeColors.neutral.line, "&:hover": { borderColor: themeColors.error.dark, color: themeColors.error.dark } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDialogSave} variant="contained" sx={{ borderRadius: 2, textTransform: "none", fontWeight: "medium", bgcolor: colors.primary.main, "&:hover": { bgcolor: colors.primary.dark }, px: 3 }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }} >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default FileUpload;